"use client";

import { useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  formatApa,
  formatFullInverted,
  type ParsedName,
  parseName,
} from "@/lib/tool-kit/reference-tools/author-name";

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

function ResultBlock({
  title,
  note,
  lines,
  copied,
  copyLabel,
  copiedLabel,
  onCopy,
}: {
  title: string;
  note: string;
  lines: string[];
  copied: boolean;
  copyLabel: string;
  copiedLabel: string;
  onCopy: () => void;
}) {
  return (
    <div className="space-y-2 rounded-lg border border-border bg-card p-5">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-medium text-foreground">{title}</h3>
        <button
          className="rounded-lg border border-border bg-background px-3 py-1.5 font-medium text-foreground text-xs transition-colors hover:bg-muted"
          onClick={onCopy}
          type="button"
        >
          {copied ? copiedLabel : copyLabel}
        </button>
      </div>
      <p className="text-muted-foreground text-xs">{note}</p>
      <pre className="overflow-x-auto rounded-lg bg-muted/50 p-4 font-mono text-foreground text-sm leading-relaxed">
        {lines.join("\n")}
      </pre>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const AUTHOR_NAME_FORMATTER_STRINGS = {
  authorNamesOnePerLine: "Author names — one per line",
  name: "one: name|other: names",
  bothJohnSmithAndSmith:
    ' — both "John Smith" and "Smith, John" forms are detected automatically',
  formatNames: "Format Names",
  clear: "Clear",
  copy: "Copy",
  copied: "Copied!",
  yes: "Yes",
  namesPlaceholder:
    "John Michael Smith\nSmith, Jane Ann\nLudwig van Beethoven\nMartin Luther King, Jr.\nJean-Michel Basquiat\nWorld Health Organization",
  apaNote:
    "Inverted with initials: Smith, J. M. Hyphenated given names keep the hyphen (J.-M.); suffixes follow the initials (King, M. L., Jr.). Titles such as Dr. and degrees such as PhD are left out, and a group author is kept as written.",
  mlaNote:
    "Inverted with the full given name: Smith, John Michael. In a multi-author MLA entry only the first author is inverted like this.",
  chicagoNote:
    "Chicago bibliography form matches MLA for a single name: Smith, John Michael. Notes (footnotes) use natural order instead.",
  formattedNames: "Formatted names",
  apa7thEdition: "APA (7th edition)",
  mla9thEdition: "MLA (9th edition)",
  chicagoBibliography: "Chicago (bibliography)",
  namesFormatted: "Names formatted",
  styles: "Styles",
  runsLocally: "Runs locally",
  howApaJoinsMultipleAuthors: "How APA joins multiple authors",
  formattingEachNameIsOnly:
    "Formatting each name is only half the job — APA also has strict rules for joining them in a reference-list entry:",
  n2Authors: "2 authors:",
  joinWithAnAmpersand: "join with an ampersand —",
  smithJM: "Smith, J. M., ",
  doeJA: " Doe, J. A.",
  n3To20Authors: "3 to 20 authors:",
  listEveryAuthorCommaSeparated:
    "list every author, comma-separated, with the ampersand before the last —",
  smithJMDoeJ: "Smith, J. M., Doe, J. A., ",
  leeK: " Lee, K.",
  n21OrMoreAuthors: "21 or more authors:",
  listTheFirst19Then:
    "list the first 19, then an ellipsis, then the final author — no ampersand —",
  smithJMDoeJ2: "Smith, J. M., Doe, J. A., ... Zhou, W.",
  noteThatEtAlBelongs: 'Note that "et al." belongs to ',
  inText: "in-text",
  citationsThreeOrMoreAuthors:
    ' citations (three or more authors are shortened to "Smith et al., 2024"), never to the reference list itself. MLA instead uses the word "and" before the final author and shortens 3+ authors to "et al." in the Works Cited entry.',
} as const;

export default function AuthorNameFormatterWidget() {
  const [input, setInput] = useState("");
  const [results, setResults] = useState<{
    apa: string[];
    mla: string[];
    chicago: string[];
  } | null>(null);
  const [copiedBlock, setCopiedBlock] = useState<string | null>(null);

  const nameCount =
    input.trim().length === 0
      ? 0
      : input.split("\n").filter((l) => l.trim().length > 0).length;

  function handleFormat() {
    const parsed = input
      .split("\n")
      .map(parseName)
      .filter((n): n is ParsedName => n !== null);
    if (parsed.length === 0) {
      return;
    }
    setResults({
      apa: parsed.map(formatApa),
      mla: parsed.map(formatFullInverted),
      chicago: parsed.map(formatFullInverted),
    });
    setCopiedBlock(null);
  }

  function handleCopy(block: string, lines: string[]) {
    writeToClipboard(lines.join("\n")).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedBlock(block);
      setTimeout(() => setCopiedBlock(null), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setResults(null);
    setCopiedBlock(null);
  }

  const locale = useToolLocale();
  const strings = useToolUi(AUTHOR_NAME_FORMATTER_STRINGS);

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="name-input"
        >
          {strings.authorNamesOnePerLine}
        </label>
        <textarea
          className="min-h-[160px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="name-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.namesPlaceholder}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {nameCount}
          {tPlural(strings.name, locale, nameCount)}
          {strings.bothJohnSmithAndSmith}
        </p>
      </div>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleFormat}
          type="button"
        >
          {strings.formatNames}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && results === null}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Results */}
      {results && (
        <section className="space-y-4">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.formattedNames}
          </h2>
          <ResultBlock
            copied={copiedBlock === "apa"}
            copiedLabel={strings.copied}
            copyLabel={strings.copy}
            lines={results.apa}
            note={strings.apaNote}
            onCopy={() => handleCopy("apa", results.apa)}
            title={strings.apa7thEdition}
          />
          <ResultBlock
            copied={copiedBlock === "mla"}
            copiedLabel={strings.copied}
            copyLabel={strings.copy}
            lines={results.mla}
            note={strings.mlaNote}
            onCopy={() => handleCopy("mla", results.mla)}
            title={strings.mla9thEdition}
          />
          <ResultBlock
            copied={copiedBlock === "chicago"}
            copiedLabel={strings.copied}
            copyLabel={strings.copy}
            lines={results.chicago}
            note={strings.chicagoNote}
            onCopy={() => handleCopy("chicago", results.chicago)}
            title={strings.chicagoBibliography}
          />
        </section>
      )}
      {/* Stats */}
      {results && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label={strings.namesFormatted} value={results.apa.length} />
          <StatCard label={strings.styles} value={3} />
          <StatCard label={strings.runsLocally} value={strings.yes} />
        </div>
      )}
      {/* Multi-author joining explainer */}
      <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.howApaJoinsMultipleAuthors}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.formattingEachNameIsOnly}
        </p>
        <ul className="space-y-3 text-muted-foreground text-sm leading-relaxed">
          <li className="rounded-lg border border-border bg-card p-4">
            <span className="font-medium text-foreground">
              {strings.n2Authors}
            </span>{" "}
            {strings.joinWithAnAmpersand}{" "}
            <span className="font-mono">
              {strings.smithJM}
              {"&"}
              {strings.doeJA}
            </span>
          </li>
          <li className="rounded-lg border border-border bg-card p-4">
            <span className="font-medium text-foreground">
              {strings.n3To20Authors}
            </span>{" "}
            {strings.listEveryAuthorCommaSeparated}{" "}
            <span className="font-mono">
              {strings.smithJMDoeJ}
              {"&"}
              {strings.leeK}
            </span>
          </li>
          <li className="rounded-lg border border-border bg-card p-4">
            <span className="font-medium text-foreground">
              {strings.n21OrMoreAuthors}
            </span>{" "}
            {strings.listTheFirst19Then}{" "}
            <span className="font-mono">{strings.smithJMDoeJ2}</span>
          </li>
        </ul>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.noteThatEtAlBelongs}
          <em>{strings.inText}</em>
          {strings.citationsThreeOrMoreAuthors}
        </p>
      </section>
    </>
  );
}
